import { useCallback, useEffect, useMemo, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import { RootState, AppDispatch } from "store";
import Pricing from "./Pricing";
import { PricingContainerProps } from "./types";
import { regionSelector, actions as regionsActions } from "reducers/regions";
import { NotificationStateType } from "reducers/types/notification";
import { actions as notificationActions } from "reducers/notification";
import { FormApi } from "final-form";
import { dirtyAlert } from "utils/alerts";
import { useTranslation } from "react-i18next";
import {
  AssuredType,
  PricingMiddlewareType,
  PricingType,
} from "reducers/types/regions";
import { actions as sessionActions } from "reducers/session";

const PricingContainer = (
  props: PricingContainerProps & ConnectorProps
): JSX.Element => {
  const { t } = useTranslation();
  const assuredDisabledValues = useMemo(
    () => ({
      amountPerHour: 0,
      minimumAmount: 0,
      minimumPercentage: 0,
      presenteeism: 0,
    }),
    []
  );
  const initialValues = useMemo(() => {
    const pricing = props.pricing;
    const values: PricingType = {
      price: {
        pickUp:
          pricing && props.filters.region?.id ? pricing.price.pickUp : null,
        delivered:
          pricing && props.filters.region?.id ? pricing.price.delivered : null,
        returned:
          pricing && props.filters.region?.id ? pricing.price.returned : null,
        extraPickUpKm:
          pricing && props.filters.region?.id
            ? pricing.price.extraPickUpKm
            : null,
        extraDeliveredKm:
          pricing && props.filters.region?.id
            ? pricing.price.extraDeliveredKm
            : null,
        extraReturnedKm:
          pricing && props.filters.region?.id
            ? pricing.price.extraReturnedKm
            : null,
        rainBonus:
          pricing && props.filters.region?.id ? pricing.price.rainBonus : null,
      },
      assured: pricing?.assured || null,
      tiers: pricing?.tiers || null,
    };
    return values;
  }, [props.filters.region?.id, props.pricing]);

  const isAssuredInitiallyEnabled = useMemo(
    () =>
      !(
        props.pricing?.assured?.amountPerHour === 0 &&
        props.pricing?.assured?.minimumAmount === 0 &&
        props.pricing?.assured?.minimumPercentage === 0 &&
        props.pricing?.assured?.presenteeism === 0
      ),
    [props.pricing?.assured]
  );

  const [isAssuredEnabled, setIsAssuredEnabled] = useState(
    isAssuredInitiallyEnabled
  );
  const [cachedAssuredValues, setCachedAssuredValues] = useState<AssuredType>(
    assuredDisabledValues
  );

  const setAssured = useCallback(() => {
    setIsAssuredEnabled(isAssuredInitiallyEnabled);
    setCachedAssuredValues(assuredDisabledValues);
  }, [assuredDisabledValues, isAssuredInitiallyEnabled]);

  useEffect(() => {
    setAssured();
  }, [setAssured]);

  const onSubmit = (values: PricingType) => {
    const payload = {
      body: values,
      id: props.filters.region?.id || "",
    };
    props.updatePricingDetailRequest(payload);
  };
  useEffect(() => {
    if (props.filters?.region)
      props.getPricingDetail(props.filters?.region?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const restore = (isDirty: boolean, form: FormApi<object, object>) => {
    const restart = () => {
      form.reset();
      setAssured();
    };
    if (isDirty) showRestoreNotification(restart);
    else restart();
  };

  const showRestoreNotification = (onClose: Function) => {
    return props.showNotification(
      dirtyAlert({
        onCancel: onClose,
        message: t("alerts:changesWillBeLostMessage"),
      })
    );
  };

  return (
    <Pricing
      {...props}
      initialValues={initialValues}
      restore={restore}
      onSubmit={onSubmit}
      isAssuredEnabled={isAssuredEnabled}
      setIsAssuredEnabled={setIsAssuredEnabled}
      cachedAssuredValues={cachedAssuredValues}
      setCachedAssuredValues={setCachedAssuredValues}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  pricing: regionSelector(state).pricing,
  filters: regionSelector(state).filters,
  region: regionSelector(state).actualRegion,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getPricingDetail: (params: string) => {
    dispatch(regionsActions.getPricingDetailRequest(params));
  },
  setDirty: (dirty: boolean) => {
    dispatch(sessionActions.setDirty(dirty));
  },
  updatePricingDetailRequest: (params: PricingMiddlewareType) => {
    dispatch(regionsActions.updatePricingDetailRequest(params));
  },
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
});
const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(PricingContainer);
